/**
 * Writes entity collections into the query cache, fanning each one across its list query and every
 * entity's detail query.
 *
 * Both writers exist to keep the cache useful without a network — a farmer opening the app in the field
 * should find recent, relevant data already there. Neither is serving an online reader, so neither makes any
 * claim about the data's freshness or validity: a fetch that was already due still happens, and a mutation's
 * invalidation still stands. `../README.md` sets out what each kind of write is allowed to claim.
 *
 * Callers pick a policy by which function they call rather than by passing flags: a seed yields to anything
 * already cached, while a patch merges over cached values but never settles the query.
 *
 * Ordering against in-flight fetches is deliberately not arbitrated here: a refetch that started before a
 * patch can resolve after it and win. The next poll corrects it, which is cheaper than cancelling fetches
 * a screen is waiting on.
 */
import type { DataTag, QueryKey } from "@tanstack/react-query";
import { queryClient } from "@/api/queryClient";

/** Every collection here is addressed by `_id`, apart from the one that overrides `toDetailId`. */
type Identifiable = { _id: string };

/**
 * A key tagged with the type cached under it. The bare `get…QueryKey()` factories return an untagged key; only
 * `get…QueryOptions().queryKey` carries the tag, and the tag is what makes a mispaired write a compile error.
 *
 * Every payload written under one is marked NoInfer, so its type comes from the key alone. Inferred from both,
 * TypeScript widens to a union that each side satisfies on its own, and a mispaired write compiles.
 */
type TaggedQueryKey<TData> = DataTag<QueryKey, TData, unknown>;

/** A list endpoint's response: a bare array, or the `{ items }` envelope the irrigation endpoints use. */
type ListResponse<T> = T[] | { items: T[] };

/** Orval's options factories for a collection, eg. getListDevicesQueryOptions and getGetDeviceQueryOptions. */
type ListQueryOptionsFactory<TList> = () => { queryKey: TaggedQueryKey<TList> };
type DetailQueryOptionsFactory<T> = (id: string) => { queryKey: TaggedQueryKey<T> };

export function seedQuery<TData>(queryKey: TaggedQueryKey<TData>, data: NoInfer<TData> | undefined) {
    // Every session field is optional; writing undefined would leave an entry reporting success with no data.
    if (data === undefined) return;

    // A seed only fills a gap: anything already cached — a fetch, a poll, an earlier seed — is at least as
    // good as session data, so it wins. Overwriting would re-stale it and force a refetch that reads as a flicker.
    // Asked of the data rather than dataUpdatedAt, which seeds set to 0 and so cannot distinguish
    // "never written" from "seeded"; keying on data also still seeds a query whose first fetch is in flight.
    if (queryClient.getQueryData(queryKey) !== undefined) return;

    // Stale on arrival, so queries still make network requests on first mount, but can fall back to cache
    queryClient.setQueryData(queryKey, data, { updatedAt: 0 });
}

/**
 * Merges poll data into one query, claiming nothing about its freshness or validity.
 *
 * A merge that returns undefined writes nothing, for a caller that cannot act on what it found cached.
 */
function patchQuery<TData>(queryKey: TaggedQueryKey<TData>, merge: (cached: TData | undefined) => TData | undefined) {
    const state = queryClient.getQueryState(queryKey);

    const next = merge(state?.data);
    if (next === undefined) return;

    // Never claims freshness: an existing refetch deadline is kept as it was, and an untouched key starts
    // stale like a seed.
    queryClient.setQueryData(queryKey, next, { updatedAt: state?.dataUpdatedAt ?? 0 });

    // Writing marks the query settled, so put back the refetch it was owed: a merge can never remove
    // anything, so it cannot answer the deletion an invalidation is often about. Exact, or a list key
    // would drag its details in too.
    if (state?.isInvalidated) {
        queryClient.invalidateQueries({ queryKey, exact: true, refetchType: "none" });
    }
}

/** Options shared by both collection writers. */
type CollectionOptions<T> = {
    /** Extracts the id the detail endpoint resolves, if it is not the default `_id` field. */
    toDetailId?: (item: T) => string | undefined;
};

/** Options for seeding a collection. */
type SeedCollectionOptions<T> = CollectionOptions<T> & {
    /** Whether the list endpoint wraps its array in an `{ items }` envelope. */
    enveloped?: boolean;
};

/**
 * Seeds a collection's list query (eg. getListDevices) and every entity's detail query for that resource (eg. getGetDevice-by-id)
 */
export function seedCollection<T extends Identifiable, TList extends ListResponse<T>>(
    listQuery: ListQueryOptionsFactory<TList>,
    detailQuery: DetailQueryOptionsFactory<T>,
    items: NoInfer<T>[] | undefined,
    { enveloped = false, toDetailId = (item) => item._id }: SeedCollectionOptions<T> = {},
) {
    // The flag decides the shape, so the list key checks the entity type but not the envelope.
    seedQuery(listQuery().queryKey, items && ((enveloped ? { items } : items) as TList));

    items?.forEach((item) => {
        const detailId = toDetailId(item);
        // No id means the endpoint cannot address the entity either, so there is no key worth seeding.
        if (detailId) seedQuery(detailQuery(detailId).queryKey, item);
    });
}

/**
 * Fans a poll's changed entities across one collection's list and detail queries.
 */
export function patchCollection<T extends Identifiable, TList extends ListResponse<T>>(
    listQuery: ListQueryOptionsFactory<TList>,
    detailQuery: DetailQueryOptionsFactory<T>,
    updates: NoInfer<T>[] | undefined,
    { toDetailId = (item) => item._id }: CollectionOptions<T> = {},
) {
    // Returning early also avoids writing a fresh array reference into the list key, which would
    // re-render every subscriber on a tick that changed nothing.
    if (!updates?.length) return;

    // Detail keys always take the patch: a poll returns whole documents, so an entity the cache has never
    // seen can still be written as it stands.
    updates.forEach((update) => {
        const detailId = toDetailId(update);
        if (!detailId) return;
        patchQuery(detailQuery(detailId).queryKey, (cached) => (cached ? { ...cached, ...update } : update));
    });

    // The list key does not, unless something has already fetched it. A poll carries only the entities that
    // changed, so writing them cold would present a handful as the whole collection, and the key carries no
    // filter to say otherwise — the same reason the seed skips Images.
    // List entries match on the detail id (an event's versions share a recordId), falling back to _id.
    const toListEntryId = (item: T) => toDetailId(item) || item._id;
    patchQuery(listQuery().queryKey, (cached) => mergeUpdatesIntoList(cached, updates, toListEntryId));
}

/** The entities in a list of either shape, or undefined when the value isn't a list at all. */
export function listItems<T>(list: ListResponse<T> | null | undefined): T[] | undefined {
    if (Array.isArray(list)) return list;
    return Array.isArray(list?.items) ? list.items : undefined;
}

/**
 * Combines polled updates with a cached list, keeping its envelope and the entities the poll did not mention.
 *
 * Returns undefined, writing nothing, for anything that is not a list: nothing cached yet, or a null body or
 * an object without items, which is left for the next fetch rather than overwritten with a handful of entities.
 */
function mergeUpdatesIntoList<T, TList extends ListResponse<T>>(
    cached: TList | undefined,
    updates: T[],
    toListEntryId: (item: T) => string,
): TList | undefined {
    const cachedItems = listItems<T>(cached);
    if (!cachedItems) return undefined;

    const updateById = new Map(updates.map((update) => [toListEntryId(update), update]));
    const cachedIds = new Set(cachedItems.map(toListEntryId));

    const items = [
        // Existing order is preserved; re-sorting would reshuffle a rendered list on every poll tick.
        ...cachedItems.map((item) => {
            const update = updateById.get(toListEntryId(item));
            return update ? { ...item, ...update } : item;
        }),
        // From the map, so two versions of one unseen entity append once, as the last one, like its detail query.
        ...[...updateById].filter(([id]) => !cachedIds.has(id)).map(([, update]) => update),
    ];

    // Narrowing a generic doesn't carry back to TList, so restate the shape the branch already established.
    return (Array.isArray(cached) ? items : { ...cached, items }) as TList;
}
